import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Button,
  Tooltip,
  InformationBox,
} from '@prio365/prio365-react-library';
import { Typography } from 'antd';
import { checkFileNameExists, fileNameRegex } from '../util';
import DebouncedInput from './DebouncedInput';
import Flex from '../../../components/Flex';
import { DriveItem } from '../../../models/Drive';
import { makePrioStyles } from '../../../theme/utils';
import { useTranslation } from 'react-i18next';

const useStyles = makePrioStyles((theme) => ({
  listIndex: {
    width: theme.spacing.large,
    fontWeight: theme.font.fontWeight.bold,
    color: theme.colors.application.typography.muted,
    marginRight: theme.spacing.extraSmall,
  },
  pdfSize: {
    color: theme.colors.application.typography.muted,
    marginLeft: theme.spacing.extraSmall,
    fontSize: theme.font.fontSize.extraSmall,
  },
  list: { width: '100%', marginTop: theme.spacing.small },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing.small,
    background: theme.colors.application.background.default,
    borderRadius: theme.old.borders.radius,
    padding: theme.spacing.small,
  },
  chevron: { cursor: 'pointer', margin: '0 8px' },
  remove: {
    color: theme.colors.base.red.default,
    cursor: 'pointer',
    marginLeft: 8,
  },
  nameLabel: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.muted,
    marginBottom: theme.spacing.small,
  },
  input: { marginBottom: theme.spacing.small },
}));

interface MergePdfDrawerProps {
  visible: boolean;
  onClose: () => void;
  onMerge: (name: string, driveItemIds: string[]) => void;
  pdfItems: DriveItem[];
  groupId: string;
  onRemoveItem?: (id: string) => void;
  merging?: boolean;
}

export const MergePdfDrawer: React.FC<MergePdfDrawerProps> = (props) => {
  //#region ------------------------------ Defaults
  const {
    visible,
    onClose: propsOnClose,
    onMerge,
    pdfItems,
    groupId,
    merging = false,
    onRemoveItem,
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [name, setName] = useState<string>('PDF_combined');
  const handleClose = () => {
    propsOnClose();
  };
  const [items, setItems] = useState<DriveItem[]>(pdfItems);
  const [checkingName, setCheckingName] = useState<boolean>(false);
  const [nameExists, setNameExists] = useState<boolean>(false);
  const [nameIsValid, setNameIsValid] = useState<boolean>(true);

  const MAX_SINGLE_SIZE = 100 * 1024 * 1024; // 100 MB
  const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500 MB
  const anyFileTooLarge = items.some(
    (item) => typeof item.size === 'number' && item.size > MAX_SINGLE_SIZE
  );
  const totalSize = items.reduce(
    (sum, item) => sum + (typeof item.size === 'number' ? item.size : 0),
    0
  );
  const totalTooLarge = totalSize > MAX_TOTAL_SIZE;
  const sizeError = anyFileTooLarge || totalTooLarge;
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const moveItem = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const newItems = [...items];
    const [moved] = newItems.splice(from, 1);
    newItems.splice(to, 0, moved);
    setItems(newItems);
  };

  const removeItem = (idx: number) => {
    const item = items[idx];
    if (onRemoveItem && item) {
      onRemoveItem(item.id);
    } else {
      setItems(items.filter((_, i) => i !== idx));
    }
  };

  const handleMerge = () => {
    if (name.trim() && items.length > 1 && nameIsValid && !nameExists) {
      onMerge(
        name.trim(),
        items.map((i) => i.id)
      );
      setName('PDF_combined');
    }
  };

  const handleNameChange = async (value: string) => {
    setName(value);
    const isValid = !!value.match(fileNameRegex) && value.trim().length > 0;
    setNameIsValid(isValid);

    const targetFolderId =
      items.length > 0 ? items[0].parentReference?.id : undefined;
    if (isValid && targetFolderId && groupId) {
      setCheckingName(true);
      const fileExists = await checkFileNameExists(
        groupId,
        targetFolderId,
        value.trim() + '.pdf'
      );
      setNameExists(fileExists);
      setCheckingName(false);
    } else {
      setNameExists(false);
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (visible) {
      setItems((prevItems) => {
        const itemIds = new Set(pdfItems.map((item) => item.id));
        const filteredItems = prevItems.filter((item) => itemIds.has(item.id));

        if (
          filteredItems.length === prevItems.length &&
          pdfItems.length !== prevItems.length
        ) {
          return pdfItems;
        }

        return filteredItems.length > 0 ? filteredItems : pdfItems;
      });
    }
  }, [visible, pdfItems]);

  useEffect(() => {
    if (!merging && visible === false) {
      setName('PDF_combined');
    }
  }, [merging, visible]);
  //#endregion

  //region ------------------------------ Render Methods
  const drawerFooter = () => {
    return (
      <>
        {sizeError && (
          <InformationBox
            type="warning"
            description={t('documents:mergePdf.maxSizeWarning')}
            marginBottom="regular"
            marginTop="small"
          />
        )}
        {(nameExists || !nameIsValid) && (
          <InformationBox
            type="error"
            description={
              !nameIsValid
                ? t('documents:confirmation.rename.invalidFileName')
                : t('documents:confirmation.rename.nameExists')
            }
            marginBottom="regular"
            marginTop="small"
          />
        )}
        <Flex.Row justifyContent="flex-end" childrenGap={8}>
          <Button type="default" onClick={handleClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleMerge}
            htmlType="submit"
            disabled={
              merging ||
              !name.trim() ||
              items.length < 2 ||
              sizeError ||
              checkingName ||
              nameExists ||
              !nameIsValid
            }
            loading={checkingName || merging}
          >
            {t('documents:mergePdf.merge')}
          </Button>
        </Flex.Row>
      </>
    );
  };

  const drawerContent = () => {
    return (
      <div>
        <div>
          <Typography.Text className={classes.nameLabel}>
            {t('documents:mergePdf.labels.name')}
          </Typography.Text>
        </div>
        <DebouncedInput
          className={classes.input}
          placeholder={t('documents:mergePdf.labels.name')}
          value={name}
          onChange={handleNameChange}
          debounceMs={300}
        />
        <div style={{ marginBottom: 4, marginTop: 16 }}>
          <Typography.Text className={classes.nameLabel}>
            {t('documents:mergePdf.labels.selectPdfs')}
          </Typography.Text>
        </div>
        <div className={classes.list}>
          {items.length === 0 && (
            <Typography.Text type="secondary">
              {t('documents:mergePdf.noPdfs')}
            </Typography.Text>
          )}
          {items.map((item, idx) => (
            <div key={item.id} className={classes.listItem}>
              <div className={classes.listIndex}>{idx + 1}.</div>
              <Typography.Text ellipsis style={{ flex: 1 }}>
                {item.name}
                {typeof item.size === 'number' && (
                  <span className={classes.pdfSize}>
                    ({(item.size / 1024).toFixed(1)} KB)
                  </span>
                )}
              </Typography.Text>
              <Button
                iconProp={['fal', 'chevron-up']}
                type="link"
                size="small"
                style={{
                  marginRight: 4,
                  opacity: idx === 0 ? 0.3 : 1,
                }}
                disabled={idx === 0}
                onClick={() => moveItem(idx, idx - 1)}
              />
              <Button
                iconProp={['fal', 'chevron-down']}
                type="link"
                size="small"
                style={{
                  marginRight: 4,
                  opacity: idx === items.length - 1 ? 0.3 : 1,
                }}
                disabled={idx === items.length - 1}
                onClick={() => moveItem(idx, idx + 1)}
              />
              <Tooltip overlay={t('common:remove')} placement="top">
                <Button
                  iconProp={['fal', 'trash']}
                  type="danger-link"
                  size="small"
                  onClick={() => removeItem(idx)}
                  style={{ marginLeft: 4 }}
                />
              </Tooltip>
            </div>
          ))}
        </div>
      </div>
    );
  };
  //endregion

  return (
    <Drawer
      title={t('documents:mergePdf.title')}
      visible={visible}
      onClose={handleClose}
      footer={drawerFooter()}
      children={drawerContent()}
    />
  );
};

export default MergePdfDrawer;
